// cityResearch.js - The trip-planning sections of a city page, from the planner's saved city research
// (public/city-research/<country>/<city>.json, written by guide-studio/city_pages.py).
// Two kinds of content stay visibly apart: our own words carry no source; facts (prices, hours, transport,
// safety, holidays) show their source and the month they were checked. Places with one of our published guides
// carry an "Audio guide" badge; every other place is plain information.
import { escapeHtml as e } from './guidesSite';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = { Mo: 'Mon', Tu: 'Tue', We: 'Wed', Th: 'Thu', Fr: 'Fri', Sa: 'Sat', Su: 'Sun', PH: 'holidays' };
const SYMBOLS = { EUR: '€', USD: '$', GBP: '£' };
// a guide stop this close to a place (and closer than to any other place) means the guide covers it;
// up to STOP_NAMED_M when every distinctive word of the place's name is in the stop's name ("Royal Palace Throne Room")
const STOP_MATCH_M = 100;
const STOP_NAMED_M = 300;
const NAME_SMALL = new Set(['of', 'the', 'de', 'del', 'la', 'el', 'los', 'las', 'do', 'da', 'di', 'and', 'y', 'e', 'a', 'st', 'san', 'santa', 'saint']);

const head = (title, em) => `${e(title)}<em>${e(em)}</em>`;
const parseDay = (iso) => { const [y, m, d] = String(iso).split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
export const checkedText = (iso) => (iso ? `checked ${MONTHS[parseDay(iso).getUTCMonth()]} ${parseDay(iso).getUTCFullYear()}` : '');
export const host = (url) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch (err) { return ''; } };
export const money = (v, cur) => {
  const n = Number(v);
  const text = Number.isInteger(n) ? String(n) : n.toFixed(2);
  return SYMBOLS[cur] ? `${SYMBOLS[cur]}${text}` : `${text} ${cur || ''}`.trim();
};
const range = (p, cur) => (p[0] === p[1] ? money(p[0], cur) : `${money(p[0], cur)}–${money(p[1], cur).replace(/^[€$£]/, '')}`);
/** [4, 5, 9, 10] → "Apr–May, Sep–Oct" */
export const monthRanges = (ms) => {
  const runs = [];
  [...ms].sort((a, b) => a - b).forEach(m => {
    const last = runs[runs.length - 1];
    if (last && m === last[1] + 1) last[1] = m; else runs.push([m, m]);
  });
  const short = (m) => MONTHS[m - 1].slice(0, 3);
  return runs.map(([a, b]) => (a === b ? short(a) : `${short(a)}–${short(b)}`)).join(', ');
};
const listOf = (names) => (names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] || ''));
const monthList = (ms) => {
  const names = ms.map(m => MONTHS[m - 1]);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] || '');
};

/** "Source: gov.uk · checked October 2026". Facts only. The source is named, never linked. */
const source = (url, checked, label = 'Source', tag = 'p') => (url
  ? `<${tag} class="lx-src">${e(label)}: ${e(host(url))}${checked ? ` · ${e(checkedText(checked))}` : ''}</${tag}>`
  : '');

/** Opening hours as people read them: "Tue–Sat 09:30–20:00 · Sun, holidays 09:30–15:00 · Mon closed". Dated exceptions are left out. */
export const readableHours = (spec) => {
  if (!spec) return '';
  if (spec.trim() === '24/7') return 'Open 24 hours';
  const rules = spec.split(';').map(r => r.trim()).filter(r => r && /^(Mo|Tu|We|Th|Fr|Sa|Su|PH)\b/.test(r));
  return rules.map(r => {
    const m = r.match(/^([A-Za-z,\- ]+?)\s+(.+)$/);
    if (!m) return '';
    const days = m[1].split(',').map(part => part.split('-').map(d => DAYS[d.trim()] || d.trim()).join('–')).join(', ');
    const times = m[2] === 'off' ? 'closed' : m[2].replace(/-/g, '–').replace(/,/g, ', ');
    return `${days} ${times}`;
  }).filter(Boolean).join(' · ');
};

const meters = (a, b) => {
  const R = 6371000, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

/**
 * Which places our published guides cover: each guide stop goes to its nearest place within STOP_MATCH_M, or else
 * to the nearest place within STOP_NAMED_M that the stop names.
 * Returns { placeId: [{ group, first }] } (first: the place is the guide's first stop, the one the sample plays).
 */
const words = (text) => String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
const names = (place, stop, city) => {
  const want = words(place).filter(w => !NAME_SMALL.has(w) && w !== city);
  const have = new Set(words(stop));
  return want.length > 0 && want.every(w => have.has(w));
};

export const audioByPlace = (places, groups, cityName = '') => {
  const out = {};
  const city = words(cityName).join(' ');
  groups.forEach(g => {
    const pts = (g.primary.points || (g.primary.firstStop ? [g.primary.firstStop] : []))
      .map(p => ({ name: p.name, lat: Number(p.lat), lng: Number(p.lng) })).filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    pts.forEach((pt, i) => {
      let best = null, bestM = Infinity;
      places.forEach(pl => { const m = meters(pt, pl); if (m < bestM) { bestM = m; best = pl; } });
      if (!best || bestM > STOP_MATCH_M) {
        best = null; bestM = Infinity;
        places.forEach(pl => { const m = meters(pt, pl); if (m <= STOP_NAMED_M && m < bestM && names(pl.name, pt.name, city)) { bestM = m; best = pl; } });
      }
      if (!best) return;
      const list = out[best.id] || (out[best.id] = []);
      const had = list.find(x => x.group === g);
      if (had) had.first = had.first || i === 0;
      else list.push({ group: g, first: i === 0 });
    });
  });
  return out;
};

/**
 * Builds the research sections. `groups` are the city's guide groups (CityPageGenerator.groupGuides);
 * place photos are expected under `photoBase`, relative to the page (copied from public/city-research/<key>/).
 */
export function researchSections(name, research, groups, photoBase = 'img/places/') {
  const r = research;
  const places = r.places || [];
  const byId = {};
  places.forEach(p => { byId[p.id] = p; });
  const audio = audioByPlace(places, groups, name);
  const badge = (id) => (audio[id] || []).map(({ group }) =>
    `<a class="lx-badge-audio" href="${e(group.primary.url)}" title="Our audio guide: ${e(group.title)}"><span aria-hidden="true">▶</span> Audio guide</a>`).join('');
  const f = r.facts || {};
  const cur = f.currency;
  const s = {};

  // ── what to see ──
  // Photo cards for the top places that have a licensed photo (two big, then rows of three), a short list for
  // the rest of the top places, and every place by area at the end. No empty picture frames.
  const minutes = (p) => (p.visit_minutes ? `about ${p.visit_minutes >= 90 ? `${Math.round(p.visit_minutes / 30) / 2} h` : `${p.visit_minutes} min`}` : '');
  const hoursOf = (p) => {
    if (p.hours && p.hours.open) return '<p class="lx-pl-hours">Open space, any time</p>';
    if (p.hours && p.hours.spec && readableHours(p.hours.spec)) {
      return `<p class="lx-pl-hours">${e(readableHours(p.hours.spec))}</p>${source(p.hours.source, p.hours.checked, 'Hours')}`;
    }
    return '';
  };
  const ourGuides = (p) => (audio[p.id] || []).length
    ? `<p class="lx-pl-ours">Hear it in ${audio[p.id].map(({ group }) => `<a href="${e(group.primary.url)}">${e(group.title)}</a>`).join(', ')}</p>` : '';
  const placeId = (p) => `place-${p.id.replace(/[^\w-]/g, '-')}`;
  const photoCard = (p, big) => {
    const ph = p.photo;
    const credit = [ph.credit, ph.license].filter(Boolean).join(', ');
    return `<article class="lx-pl${big ? ' lx-pl-big' : ''}${audio[p.id] ? ' has-audio' : ''}" id="${e(placeId(p))}">`
      + `<div class="lx-pl-img"><img src="${e(photoBase + ph.file)}" alt="${e(p.name)}, ${e(name)}" loading="lazy" decoding="async" width="${ph.width}" height="${ph.height}">`
      + `${badge(p.id) ? `<span class="lx-pl-flag">${badge(p.id)}</span>` : ''}`
      + `<a class="lx-credit" href="${e(ph.page || ph.license_url || '#')}" rel="nofollow noopener" target="_blank" title="Photo: ${e(credit)}">Photo: ${e(credit)}</a></div>`
      + `<div class="lx-pl-body"><p class="lx-pl-meta">${e([p.area, minutes(p)].filter(Boolean).join(' · '))}</p>`
      + `<h3>${e(p.name)}</h3>${p.summary ? `<p class="lx-pl-sum">${e(p.summary)}</p>` : ''}${ourGuides(p)}`
      + `<div class="lx-pl-foot">${hoursOf(p)}</div></div></article>`;
  };
  const top = (r.top || []).map(id => byId[id]).filter(Boolean);
  const withPhoto = top.filter(p => p.photo);
  const big = Math.min(2, withPhoto.length);
  // when cards must go to keep whole rows, places without our audio go first (from the end of the list)
  const slots = big + Math.min(6, Math.floor((withPhoto.length - big) / 3) * 3);
  const shown = [...withPhoto];
  for (let i = shown.length - 1; shown.length > slots && i >= big; i -= 1) if (!audio[shown[i].id]) shown.splice(i, 1);
  shown.splice(slots);
  const shownIds = new Set(shown.map(p => p.id));
  const also = top.filter(p => !shownIds.has(p.id));
  const alsoHtml = also.length
    ? `<div class="lx-also"><h3>Also worth seeing</h3><ul>${also.map(p => `<li id="${e(placeId(p))}"><b>${e(p.name)}</b>${badge(p.id)}`
      + `<span>${e([p.area, minutes(p)].filter(Boolean).join(' · '))}</span>${p.summary ? `<p>${e(p.summary)}</p>` : ''}</li>`).join('')}</ul></div>`
    : '';
  const topIds = new Set(top.map(p => p.id));
  const rest = places.filter(p => !topIds.has(p.id));
  const byArea = {};
  rest.forEach(p => { const a = p.area || 'Elsewhere'; (byArea[a] = byArea[a] || []).push(p); });
  const restHtml = rest.length
    ? `<details class="lx-more"><summary>All ${places.length} places worth knowing, by area</summary><div class="lx-more-in">`
      + Object.keys(byArea).sort().map(a => `<div><h3>${e(a)}</h3><ul>${byArea[a].map(p => `<li>${e(p.name)}${badge(p.id)}</li>`).join('')}</ul></div>`).join('')
      + `</div></details>`
    : '';
  const nAudio = places.filter(p => audio[p.id]).length;
  if (top.length) {
    s.see = `
  <section class="lx-sec" id="see">
    <div class="lx-head"><p class="lx-kicker">What to see</p><h2>${head(`${name}'s `, 'must-sees')}</h2>
    <p>The places we would put in any first visit.${nAudio ? ' <b class="lx-badge-audio lx-badge-static"><span aria-hidden="true">▶</span> Audio guide</b> marks the ones our guides tell the story of.' : ''}</p></div>
    ${shown.length ? `<div class="lx-pls">${shown.map((p, i) => photoCard(p, i < big)).join('')}</div>` : ''}
    ${alsoHtml}
    ${restHtml}
  </section>`;
  }

  // ── where to stay: a map of the neighbourhoods ──
  // The map image (city_pages.city_map) is OpenStreetMap, drawn once; the marks are positioned on it here, in % so
  // labels stay readable at any width. Red circles are official caution zones (with their source); "Good base"
  // marks come from a rule (most must-sees within a 20-minute walk, not in a caution zone). Nothing is called safe.
  // `extraAreas` are the content file's hand-written areas: [{ name, text, groups }]; their text wins over ours.
  s.areaMap = (extraAreas = []) => {
    const m = r.map;   // without a map image, the section is the list alone
    const world = (lat, lng) => {
      const size = 256 * 2 ** m.zoom;
      const y = Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 180) / 2));
      return [(lng + 180) / 360 * size - m.x0, (1 - y / Math.PI) / 2 * size - m.y0];
    };
    const at = (lat, lng) => {
      if (!m) return null;
      const [x, y] = world(lat, lng);
      return x >= 0 && y >= 0 && x <= m.width && y <= m.height ? `left:${(x / m.width * 100).toFixed(2)}%;top:${(y / m.height * 100).toFixed(2)}%` : null;
    };
    const areas = (r.neighbourhoods || []).map(n => ({
      name: n.name, text: n.character, center: n.center, stay: !!n.stay, caution: n.caution || [], near: n.near_top,
      places: n.places.map(id => byId[id]).filter(p => p && p.name.toLowerCase() !== n.name.toLowerCase()), groups: [],
    }));
    extraAreas.forEach(x => {
      const same = areas.find(a => a.name.toLowerCase() === x.name.toLowerCase());
      if (same) { same.text = x.text || same.text; same.groups = x.groups; return; }
      // placed where its guides' stops are
      const pts = x.groups.flatMap(g => g.primary.points || (g.primary.firstStop ? [g.primary.firstStop] : []))
        .map(p => [Number(p.lat), Number(p.lng)]).filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b));
      const center = pts.length ? { lat: pts.reduce((t, p) => t + p[0], 0) / pts.length, lng: pts.reduce((t, p) => t + p[1], 0) / pts.length } : null;
      areas.unshift({ name: x.name, text: x.text, center, stay: false, caution: [], places: [], groups: x.groups });
    });

    const zones = (f.areas || []).filter(z => z.lat != null && z.radius_m);
    // real outlines (city_pages.add_shapes) in map pixels, drawn in one SVG that scales with the image;
    // a zone without an outline keeps its circle
    const shapes = f.zone_shapes || {};
    const svg = m ? `<svg class="lx-mshapes" viewBox="0 0 ${m.width} ${m.height}" preserveAspectRatio="none" aria-hidden="true">`
      + (r.neighbourhoods || []).filter(n => n.shape).map(n => `<path class="lx-mnb${n.stay ? ' is-stay' : ''}" d="${e(n.shape)}"><title>${e(n.name)}</title></path>`).join('')
      + zones.filter(z => shapes[z.name]).map(z => `<path class="lx-mzone is-${e(shapes[z.name].kind)}" d="${e(shapes[z.name].d)}"><title>${e(z.name)}: ${e(z.advice)}</title></path>`).join('')
      + `</svg>` : '';
    const zoneHtml = zones.map(z => {
      const pos = at(z.lat, z.lng);
      if (!pos) return '';
      if (shapes[z.name]) {
        // the outline is drawn; only its name tag goes here, at the zone's point
        return `<span class="lx-mz-tag" style="${pos}">${e(z.name)}</span>`;
      }
      const mPerPx = 156543.03392 * Math.cos(z.lat * Math.PI / 180) / 2 ** m.zoom;
      return `<span class="lx-mz" style="${pos};width:${(2 * z.radius_m / mPerPx / m.width * 100).toFixed(2)}%" title="${e(z.name)}: ${e(z.advice)}"><b>${e(z.name)}</b></span>`;
    }).join('');
    const dotHtml = (r.top || []).map(id => byId[id]).filter(Boolean).map(p => {
      const pos = at(p.lat, p.lng);
      return pos ? `<span class="lx-md${audio[p.id] ? ' has-audio' : ''}" style="${pos}" title="${e(p.name)}"></span>` : '';
    }).join('');
    const labelHtml = areas.map(a => {
      const pos = a.center && at(a.center.lat, a.center.lng);
      if (!pos) return '';
      // a label near a side of the map grows inwards, so it is never cut off
      const x = world(a.center.lat, a.center.lng)[0] / m.width;   // only reached with a map
      const edge = x < 0.12 ? ' is-left' : (x > 0.88 ? ' is-right' : '');
      return pos ? `<span class="lx-ml${a.stay ? ' is-stay' : ''}${edge}" style="${pos}">${a.stay ? '<i aria-hidden="true">★</i>' : ''}${e(a.name)}</span>` : '';
    }).join('');
    const zoneSources = [...new Set(zones.map(z => z.source))];

    // the good bases first, then the rest as written
    const cards = [...areas.filter(a => a.stay), ...areas.filter(a => !a.stay)].map(a => {
      const tags = [a.stay ? `<span class="lx-tag-stay">★ Good base to stay</span>` : '',
        ...a.caution.map(c => `<span class="lx-tag-caution">In an official caution zone: ${e(c)}</span>`)].join('');
      const guides = a.groups.map(g => `<li><a href="${e(g.primary.url)}"><span aria-hidden="true">▶</span> ${e(g.title)}</a></li>`).join('');
      const pl = a.places.map(p => `${e(p.name)}${badge(p.id)}`).join(', ');
      return `<div class="lx-nb${a.stay ? ' is-stay' : ''}"><h3>${e(a.name)}</h3>${tags ? `<p class="lx-nb-tags">${tags}</p>` : ''}`
        + `${a.text ? `<p>${e(a.text)}</p>` : ''}${a.stay && a.near ? `<p class="lx-nb-why">${a.near} of the must-sees within about a 20-minute walk.</p>` : ''}`
        + `${pl ? `<p class="lx-nb-places">${pl}</p>` : ''}${guides ? `<ul class="lx-nb-guides">${guides}</ul>` : ''}</div>`;
    }).join('');
    const stays = areas.filter(a => a.stay).map(a => a.name);
    if (!cards) return '';
    const intro = (stays.length ? `For a first visit, the best bases are ${e(listOf(stays))}: the most must-sees within an easy walk.` : 'The parts of town worth knowing.')
      + (m && zones.length ? ' Red marks the places official travel advice names as pickpocketing hotspots: keep your bag closed there.' : '');
    const figure = m ? `
    <figure class="lx-map">
      <div class="lx-map-in" style="aspect-ratio:${m.width}/${m.height}">
        <img src="${e(photoBase + m.file)}" alt="Map of ${e(name)} with its neighbourhoods" loading="lazy" decoding="async" width="${m.width}" height="${m.height}">
        ${svg}${zoneHtml}${dotHtml}${labelHtml}
      </div>
      <figcaption><span><i class="lx-key lx-key-nb"></i> Good base to stay</span>${zones.length ? '<span><i class="lx-key lx-key-zone"></i> Official caution zone</span>' : ''}<span><i class="lx-key lx-key-dot"></i> Must-see</span>${nAudio ? '<span><i class="lx-key lx-key-dot has-audio"></i> In our audio guides</span>' : ''}
      <span class="lx-map-src">${zoneSources.length ? `Caution zones: ${e(zoneSources.map(host).join(', '))}${f.advice_checked ? `, ${e(checkedText(f.advice_checked))}` : ''} · ` : ''}Map <a href="${e(m.attribution_url)}" rel="nofollow noopener" target="_blank">${e(m.attribution)}</a></span></figcaption>
    </figure>` : '';
    return `
  <section class="lx-sec" id="areas">
    <div class="lx-head"><p class="lx-kicker">Where to stay</p><h2>${head(`${name}, `, m ? 'on the map' : 'neighbourhood by neighbourhood')}</h2>
    <p>${intro}</p></div>${figure}
    <div class="lx-nbs">${cards}</div>
  </section>`;
  };

  // ── when to go ──
  const c = r.climate;
  if (c && c.months) {
    const ms = Array.from({ length: 12 }, (_, i) => ({ m: i + 1, ...(c.months[String(i + 1)] || {}) }));
    const hi = Math.max(...ms.map(x => x.tmax || 0), 30);
    const best = new Set(c.best_months || []);
    const cells = ms.map(x => `<div class="lx-cm${best.has(x.m) ? ' is-best' : ''}"><span class="lx-cm-bar" style="--h:${Math.max(4, Math.round((x.tmax || 0) / hi * 100))}%"><i>${Math.round(x.tmax)}°</i></span>`
      + `<small>${Math.round(x.tmin)}°</small><b>${MONTHS[x.m - 1].slice(0, 3)}</b><em title="Rainy days">${Math.round(x.rainy_days)} d</em></div>`).join('');
    const hol = (r.holidays || []).map(h => {
      const d = parseDay(h.date);
      return `<li><time datetime="${e(h.date)}">${d.getUTCDate()} ${MONTHS[d.getUTCMonth()].slice(0, 3)} ${d.getUTCFullYear()}</time> ${e(h.name)}</li>`;
    }).join('');
    s.when = `
  <section class="lx-sec lx-sec-paper" id="when">
    <div class="lx-head"><p class="lx-kicker">When to go</p><h2>${head(`${name}, `, 'month by month')}</h2>
    ${best.size ? `<p>The most comfortable months for walking are ${e(monthList([...best].sort((a, b) => a - b)))}.</p>` : ''}</div>
    <div class="lx-when">
      <div class="lx-fact"><h3>Weather</h3><div class="lx-climate" role="img" aria-label="Average daytime highs, night lows and rainy days per month">${cells}</div>
      <p class="lx-legend"><span class="lx-key-best"></span> best months · bar: average high · small: average low · d: days with rain</p>
      <p class="lx-src">Source: ${e(c.source || 'historical weather data')}</p></div>
      ${hol ? `<div class="lx-fact"><h3>Public holidays</h3><p class="lx-note">Shops, offices and some sights close or keep Sunday hours.</p><ul class="lx-hol">${hol}</ul>${source(r.holidays_source, r.exported && r.exported.slice(0, 10), 'Dates')}</div>` : ''}
    </div>
  </section>`;
  }

  // ── getting there and around ──
  const g = r.guide || {};
  const bullets = (lines) => (lines && lines.length ? `<ul class="lx-words">${lines.map(l => `<li>${e(l)}</li>`).join('')}</ul>` : '');
  const ap = r.airport;
  const apFacts = [
    ...((ap && ap.options) || []).map(o => `<div class="lx-fact"><h3>${e(o.label)} from ${e(ap.code)}</h3><p class="lx-big">${e(range(o.price, o.currency))}</p><p>About ${o.minutes} minutes to the centre. Our estimate from the published rates, not a fixed fare.</p>${source(o.source, ap.checked)}</div>`),
    ...(f.airport_fixed_fares || []).map(x => `<div class="lx-fact"><h3>${e(x.label || x.name || 'Fixed fare')}</h3><p class="lx-big">${e(money(x.price || x.fare, cur))}</p>${x.note ? `<p>${e(x.note)}</p>` : ''}${source(x.source_url, x.checked)}</div>`),
  ].join('');
  const aroundFacts = [
    f.transit ? `<div class="lx-fact"><h3>${e(f.transit.name)}</h3><p class="lx-big">${e(money(f.transit.single_fare, cur))}</p><p>A single ticket.</p>${source(f.transit.source, f.transit.checked)}</div>` : '',
    f.taxi ? `<div class="lx-fact"><h3>Taxi rates</h3><p class="lx-big">${e(money(f.taxi.per_km, cur))} / km</p><p>${[f.taxi.base ? `Start ${money(f.taxi.base, cur)}` : '', f.taxi.minimum ? `minimum ${money(f.taxi.minimum, cur)}` : ''].filter(Boolean).join(', ')}</p>${source(f.taxi.source, f.taxi.checked)}</div>` : '',
    (f.ride_services || []).length ? `<div class="lx-fact"><h3>Ride apps</h3><p>${e(f.ride_services.join(', '))} work in ${e(name)}.</p>${source(f.ride_services_source, f.transit && f.transit.checked)}</div>` : '',
  ].join('');
  if (g.getting_there || g.getting_around || apFacts || aroundFacts) {
    s.transport = `
  <section class="lx-sec" id="transport">
    <div class="lx-head"><p class="lx-kicker">Getting there &amp; around</p><h2>${head('From the airport ', 'and across town')}</h2>
    <p>Our advice first; prices only where we checked them on an official or operator page.</p></div>
    <div class="lx-cols">
      <div><h3 class="lx-col-title">${ap ? `From ${e(ap.name || ap.code)}` : 'Arriving'}</h3>${bullets(g.getting_there)}<div class="lx-facts">${apFacts}</div></div>
      <div><h3 class="lx-col-title">Getting around</h3>${bullets(g.getting_around)}<div class="lx-facts">${aroundFacts}</div></div>
    </div>
  </section>`;
  }

  // ── money, customs and safety ──
  const em = (f.emergency || []).map(x => `<div class="lx-fact lx-sos"><p class="lx-big">${e(x.number)}</p><p>${e(x.service)}</p>${source(x.source, f.advice_checked)}</div>`).join('');
  // one source line when every tip comes from the same site, otherwise a source per tip
  const adviceSources = [...new Set([...(f.safety_tips || []), ...(f.areas || [])].map(t => t.source))];
  const each = adviceSources.length > 1;
  const official = (f.safety_tips || []).map(t => `<li>${e(t.text)}${each ? ` ${source(t.source, null, 'Source', 'span')}` : ''}</li>`).join('');
  const areas = (f.areas || []).map(a => `<li><b>${e(a.name)}.</b> ${e(a.advice)}${each ? ` ${source(a.source, null, 'Source', 'span')}` : ''}</li>`).join('');
  if (g.money_and_customs || g.safety || g.tips || em || official) {
    s.safety = `
  <section class="lx-sec lx-sec-paper" id="safety">
    <div class="lx-head"><p class="lx-kicker">Money, customs &amp; safety</p><h2>${head('Good to know ', `before ${name}`)}</h2></div>
    <div class="lx-cols">
      <div>${g.money_and_customs && g.money_and_customs.length ? `<h3 class="lx-col-title">Money and customs</h3>${bullets(g.money_and_customs)}` : ''}
      ${g.safety && g.safety.length ? `<h3 class="lx-col-title">Staying safe</h3>${bullets(g.safety)}` : ''}
      ${g.tips && g.tips.length ? `<h3 class="lx-col-title">Our tips</h3>${bullets(g.tips)}` : ''}</div>
      <div>${em ? `<h3 class="lx-col-title">Emergency</h3><div class="lx-facts">${em}</div>` : ''}
      ${official || areas ? `<div class="lx-fact"><h3>Official travel advice</h3><ul class="lx-official">${official}${areas}</ul>${each ? (f.advice_checked ? `<p class="lx-src">${e(checkedText(f.advice_checked))}</p>` : '') : source(adviceSources[0], f.advice_checked)}</div>` : ''}</div>
    </div>
  </section>`;
  }

  s.faq = (g.faq || []).map(x => [x.question, x.answer]);
  s.stats = {
    places: places.length,
    best: monthRanges((c && c.best_months) || []),
    days: g.trip_length_days,
    audioPlaces: nAudio,
    checked: (f.transit && f.transit.checked) || f.advice_checked || r.built,
  };
  s.topNames = top.map(p => p.name);
  // the header photo for a city without guides of ours: the first must-see with a licensed photo
  s.heroPhoto = withPhoto.length ? photoBase + withPhoto[0].photo.file : '';
  return s;
}
